import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Building2,
  CalendarDays,
  Eye,
  ExternalLink,
  Hash,
  Package,
  Phone,
  User,
  Wallet,
} from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Section } from '@/shared/ui/layout/Section';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { CargoIdTypeBadge } from '@/shared/ui/chips/CargoIdTypeBadge';
import { StatusChip } from '@/shared/ui/chips/StatusChip';
import { extractErrorMessage } from '@/shared/api/errors';
import { useCargoIdDetail, useCargoIdParcels } from '@/entities/cargo-id';
import { useCharges } from '@/entities/charges';
import { formatDate, formatDateTime } from '@/shared/utils/date';
import { formatUsd } from '@/shared/utils/currency';
import { FeaturesChargesTable } from '@/features/charges';
import type { ApiParcelsGetItem } from '@/shared/types/api/parcels';

const PAGE_SIZE = 20;

const isFilled = (value: string | null | undefined): boolean => Boolean(value?.trim());

export const WidgetsCargoIdDetailMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();

  const [parcelsPage, setParcelsPage] = useState(1);

  const query = useCargoIdDetail(id);
  const parcelsQuery = useCargoIdParcels(id, parcelsPage);
  const chargesQuery = useCharges({ cargo_id: id, page_size: 100 });

  const parcelColumns = useMemo<ColumnDef<ApiParcelsGetItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('cargoIds.detail.parcels.columns.name'),
        cell: (info) => (
          <span className="font-medium text-ink">{info.row.original.name}</span>
        ),
      },
      {
        accessorKey: 'trek_id',
        header: t('cargoIds.detail.parcels.columns.trekId'),
        cell: (info) => <span className="font-mono text-xs">{info.row.original.trek_id}</span>,
      },
      {
        accessorKey: 'status',
        header: t('cargoIds.detail.parcels.columns.status'),
        cell: (info) => <StatusChip status={info.row.original.status} />,
      },
      {
        accessorKey: 'total_amount',
        header: t('cargoIds.detail.parcels.columns.total'),
        cell: (info) => (
          <span className="font-medium">{formatUsd(info.row.original.total_amount)}</span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: t('cargoIds.detail.parcels.columns.createdAt'),
        cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        id: 'actions',
        header: t('cargoIds.detail.parcels.columns.actions'),
        enableSorting: false,
        cell: (info) => (
          <IconButton
            label={t('cargoIds.detail.parcels.view')}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/parcels/${info.row.original.id}`);
            }}
          >
            <Eye className="h-4 w-4" />
          </IconButton>
        ),
      },
    ],
    [navigate, t],
  );

  if (query.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <div className="p-6">
        <ErrorState
          message={extractErrorMessage(query.error, t('cargoIds.detail.notFound'))}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const cargoId = query.data;
  const empty = <span className="text-ink-faint italic">{t('cargoIds.detail.empty')}</span>;

  // Owner rule (#60, 2026-05-31): VILOYAT (region) row dropped from the
  // admin Cargo ID detail page. The region is implied by the code prefix.
  // Owner rule (2026-05-31): Tur (Jismoniy/Yuridik) row added so admins can
  // see at a glance which Cargo IDs bypass the PINFL spending limit.
  const rows: { key: string; icon: typeof User; label: string; value: ReactNode }[] = [
    {
      key: 'fullName',
      icon: User,
      label: t('cargoIds.detail.fields.fullName'),
      value: isFilled(cargoId.full_name) ? cargoId.full_name : empty,
    },
    {
      key: 'phone',
      icon: Phone,
      label: t('cargoIds.detail.fields.phone'),
      value: isFilled(cargoId.phone_number) ? cargoId.phone_number : empty,
    },
    {
      key: 'type',
      icon: Building2,
      label: t('cargoIds.detail.fields.type'),
      value: t(`cargoIdType.${cargoId.entity_type}`),
    },
    {
      key: 'createdAt',
      icon: CalendarDays,
      label: t('cargoIds.detail.fields.createdAt'),
      value: formatDateTime(cargoId.created_at),
    },
  ];

  return (
    <>
      <PageHeader
        title={cargoId.code ?? t('cargoIds.detail.noCode')}
        subtitle={t('cargoIds.detail.subtitle')}
        showBack
        backTo="/cargo-ids"
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <Section>
          <div className="flex flex-wrap items-center gap-2">
            <CargoIdTypeBadge type={cargoId.entity_type} />
            <span className="ml-auto inline-flex items-center gap-1.5 text-sm text-ink-muted">
              <Hash className="h-4 w-4" aria-hidden />
              {cargoId.code ?? t('cargoIds.detail.noCode')}
            </span>
          </div>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {rows.map(({ key, icon: Icon, label, value }) => (
              <div key={key} className="flex items-start gap-3">
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
                <div className="min-w-0">
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                    {label}
                  </dt>
                  <dd className="break-words text-sm text-ink-soft">{value}</dd>
                </div>
              </div>
            ))}
          </dl>
        </Section>

        <Section>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <User className="h-4 w-4 text-ink-muted" aria-hidden />
            {t('cargoIds.detail.owner.title')}
          </h2>
          {cargoId.owner ? (
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                {
                  key: 'fullName',
                  icon: User,
                  label: t('cargoIds.detail.owner.fullName'),
                  value: isFilled(cargoId.owner.full_name) ? cargoId.owner.full_name : empty,
                },
                {
                  key: 'phone',
                  icon: Phone,
                  label: t('cargoIds.detail.owner.phone'),
                  value: isFilled(cargoId.owner.phone_number) ? cargoId.owner.phone_number : empty,
                },
              ].map(({ key, icon: Icon, label, value }) => (
                <div key={key} className="flex items-start gap-3">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
                  <div className="min-w-0">
                    <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                      {label}
                    </dt>
                    <dd className="break-words text-sm text-ink-soft">{value}</dd>
                  </div>
                </div>
              ))}
              <div className="sm:col-span-2">
                <Link
                  to={`/clients/${cargoId.owner.id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  {t('cargoIds.detail.owner.openClient')}
                </Link>
              </div>
            </dl>
          ) : (
            <span className="text-sm italic text-ink-faint">
              {t('cargoIds.detail.owner.empty')}
            </span>
          )}
        </Section>

        <Section>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <Wallet className="h-4 w-4 text-ink-muted" aria-hidden />
            {t('cargoIds.detail.charges.title')}
            <span
              className={`ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-xs ${
                Number(cargoId.debt) > 0 ? 'text-amber-600 font-semibold' : 'text-ink-soft'
              }`}
            >
              {formatUsd(Number(cargoId.debt))}
            </span>
          </h2>
          {chargesQuery.error ? (
            <ErrorState
              message={extractErrorMessage(
                chargesQuery.error,
                t('cargoIds.detail.charges.loadError'),
              )}
              onRetry={() => void chargesQuery.refetch()}
            />
          ) : (
            <FeaturesChargesTable
              charges={chargesQuery.data ?? []}
              isLoading={chargesQuery.isLoading}
            />
          )}
        </Section>

        <Section>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <Package className="h-4 w-4 text-ink-muted" aria-hidden />
            {t('cargoIds.detail.parcels.title')}
            <span className="rounded-full bg-slate-100 px-1.5 text-xs text-ink-soft">
              {cargoId.parcels_count}
            </span>
          </h2>
          {parcelsQuery.error ? (
            <ErrorState
              message={extractErrorMessage(parcelsQuery.error, t('cargoIds.detail.parcels.loadError'))}
              onRetry={() => void parcelsQuery.refetch()}
            />
          ) : (
            <>
              <DataTable
                data={parcelsQuery.data?.results ?? []}
                columns={parcelColumns}
                isLoading={parcelsQuery.isLoading}
                emptyTitle={t('cargoIds.detail.parcels.emptyTitle')}
                emptyIcon={<Package className="h-8 w-8" aria-hidden />}
                onRowClick={(row) => navigate(`/parcels/${row.id}`)}
                getRowId={(row) => row.id}
                numbered
                pageIndex={parcelsPage - 1}
                pageSize={PAGE_SIZE}
              />
              <Pagination
                page={parcelsPage}
                pageSize={PAGE_SIZE}
                total={parcelsQuery.data?.count ?? 0}
                onPageChange={setParcelsPage}
              />
            </>
          )}
        </Section>
      </div>
    </>
  );
};
